/**
 * AI性能视图
 * 层面二：AI核心性能
 */

import React from 'react';
import { Brain, Zap, Target, AlertTriangle } from 'lucide-react';
import MetricCard from '../charts/MetricCard';
import CompositeMetricGauge from '../charts/CompositeMetricGauge';
import TrendChart from '../charts/TrendChart';
import ScatterPlot from '../charts/ScatterPlot';
// Using any for now to fix TypeScript compilation  
type AiCorePerformanceMetrics = any;
type TimeSeriesDataPoint = any;
type TrendAnalysis = any;

interface AiPerformanceViewProps {
  aiCorePerformance: AiCorePerformanceMetrics;
  aiPerformance: any;
  apiLatency: any;
  timeSeries: {
    aiAccuracy: TimeSeriesDataPoint[];
    manualCorrectionEffort: TimeSeriesDataPoint[];
  };
  trends: {
    aiAccuracy: TrendAnalysis;
  };
}

const AiPerformanceView: React.FC<AiPerformanceViewProps> = ({
  aiCorePerformance,
  aiPerformance,
  apiLatency,
  timeSeries,
  trends
}) => {
  // MCE组件分解
  const mceComponents = [
    {
      name: '拖拽调整',
      value: aiCorePerformance.details.dragAdjustments,
      weight: 1,
      color: '#10b981' // green
    },
    {
      name: '标签修改',
      value: aiCorePerformance.details.labelModifications,
      weight: 3,
      color: '#f59e0b' // amber
    },
    {
      name: '手动新增',
      value: aiCorePerformance.details.manualAcupointAdditions,
      weight: 5,
      color: '#ef4444' // red
    }
  ];

  // 获取AI准确率告警
  const getAccuracyAlert = () => {
    if (aiPerformance.accuracyRate < 0.7) {
      return {
        level: 'critical' as const,
        message: 'AI准确率过低，需要紧急优化模型'
      };
    }
    if (aiPerformance.accuracyRate < 0.85) {
      return {
        level: 'warning' as const,
        message: 'AI准确率偏低，建议重新训练模型'
      };
    }
    return undefined;
  };

  // 获取MCE告警
  const getMceAlert = () => {
    if (aiCorePerformance.manualCorrectionEffortIndex > 1000) {
      return {
        level: 'critical' as const,
        message: '手动修正工作量过大，严重影响用户体验'
      };
    }
    if (aiCorePerformance.manualCorrectionEffortIndex > 500) {
      return {
        level: 'warning' as const,
        message: '手动修正工作量较大，建议优化模型精度'
      };
    }
    return undefined;
  };

  // 获取自信度校准告警
  const getCalibrationAlert = () => {
    if (aiCorePerformance.confidenceCalibrationScore < 0.6) {
      return {
        level: 'critical' as const,
        message: 'AI自信度校准严重偏差，影响用户信任'
      };
    }
    if (aiCorePerformance.confidenceCalibrationScore < 0.8) {
      return {
        level: 'warning' as const,
        message: 'AI自信度校准需要优化'
      };
    }
    return undefined;
  };

  // 获取API延迟告警
  const getLatencyAlert = () => {
    if (apiLatency.p95Latency > 5000) {
      return {
        level: 'critical' as const,
        message: 'API响应时间过长，严重影响用户体验'
      };
    }
    if (apiLatency.p95Latency > 3000) {
      return {
        level: 'warning' as const,
        message: 'API响应时间偏长，建议优化'
      };
    }
    return undefined;
  };

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">AI核心性能</h2>
        <p className="text-gray-600 mt-1">
          监控AI模型准确性、用户修正成本和系统响应性能
        </p>
      </div>

      {/* 核心指标卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="AI准确率"
          value={aiPerformance.accuracyRate}
          format="percentage"
          subtitle={`基于${aiPerformance.totalPredictions.toLocaleString()}个预测`}
          trend={{
            direction: trends.aiAccuracy.trend === 'increasing' ? 'up' : 
                      trends.aiAccuracy.trend === 'decreasing' ? 'down' : 'stable',
            value: Math.abs(trends.aiAccuracy.changeRate),
            label: '相比上期'
          }}
          alert={getAccuracyAlert()}
          icon={Target}
        />

        <MetricCard
          title="手动修正精力指数"
          value={aiCorePerformance.manualCorrectionEffortIndex}
          format="number"
          subtitle="用户修正工作量"
          alert={getMceAlert()}
          icon={AlertTriangle}
        />

        <MetricCard
          title="自信度校准分数"
          value={aiCorePerformance.confidenceCalibrationScore}
          format="percentage"
          subtitle="AI自信度与实际准确性的一致性"
          alert={getCalibrationAlert()}
          icon={Brain}
        />

        <MetricCard
          title="P95 API延迟"
          value={apiLatency.p95Latency}
          format="duration"
          subtitle={`基于${apiLatency.totalRequests.toLocaleString()}个请求`}
          alert={getLatencyAlert()}
          icon={Zap}
        />
      </div>

      {/* 复合指标仪表盘 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 手动修正精力指数分解 */}
        <CompositeMetricGauge
          title="手动修正精力指数 (MCE)"
          value={aiCorePerformance.manualCorrectionEffortIndex}
          maxValue={2000}
          description="评估用户在修正AI结果上的工作量"
          components={mceComponents}
          thresholds={{
            excellent: 200,
            good: 500,
            warning: 1000
          }}
        />

        {/* AI性能综合指标 */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">AI性能综合指标</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">准确率</span>
              <div className="flex items-center">
                <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                  <div 
                    className="bg-blue-600 h-2 rounded-full"
                    style={{ width: `${aiPerformance.accuracyRate * 100}%` }}
                  ></div>
                </div>
                <span className="text-sm font-medium">
                  {(aiPerformance.accuracyRate * 100).toFixed(1)}%
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">召回率</span>
              <div className="flex items-center">
                <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                  <div 
                    className="bg-green-600 h-2 rounded-full"
                    style={{ width: `${aiPerformance.recallRate * 100}%` }}
                  ></div>
                </div>
                <span className="text-sm font-medium">
                  {(aiPerformance.recallRate * 100).toFixed(1)}%
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">平均自信度</span>
              <div className="flex items-center">
                <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                  <div 
                    className="bg-purple-600 h-2 rounded-full"
                    style={{ width: `${aiCorePerformance.details.averageConfidenceScore * 100}%` }}
                  ></div>
                </div>
                <span className="text-sm font-medium">
                  {(aiCorePerformance.details.averageConfidenceScore * 100).toFixed(1)}%
                </span>
              </div>
            </div>

            <div className="pt-3 border-t border-gray-200">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-gray-600">总预测数</div>
                  <div className="font-medium">{aiPerformance.totalPredictions.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-gray-600">总修正数</div>
                  <div className="font-medium">{aiPerformance.totalCorrections.toLocaleString()}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* AI自信度校准散点图 */}
      <ScatterPlot
        title="AI自信度与实际准确率校准分析"
        data={aiCorePerformance.confidenceCalibrationData}
        height={400}
      />

      {/* 趋势图表 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* AI准确率趋势 */}
        <TrendChart
          title="AI准确率趋势"
          data={timeSeries.aiAccuracy}
          chartType="line"
          color="#10b981"
          formatValue={(value) => `${(value * 100).toFixed(1)}%`}
          formatDate={(timestamp) => new Date(timestamp).toLocaleDateString('zh-CN', { 
            month: 'short', 
            day: 'numeric' 
          })}
          trendAnalysis={trends.aiAccuracy}
          thresholds={{
            warning: 0.85,
            critical: 0.7
          }}
        />

        {/* 手动修正工作量趋势 */}
        <TrendChart
          title="手动修正精力指数趋势"
          data={timeSeries.manualCorrectionEffort}
          chartType="bar"
          color="#ef4444"
          formatValue={(value) => value.toFixed(0)}
          formatDate={(timestamp) => new Date(timestamp).toLocaleDateString('zh-CN', { 
            month: 'short', 
            day: 'numeric' 
          })}
          thresholds={{
            warning: 500,
            critical: 1000
          }}
        />
      </div>

      {/* 常见误识别分析 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 常见误识别穴位 */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">常见误识别穴位</h3>
          <div className="space-y-3">
            {aiPerformance.commonMisidentifications.slice(0, 8).map((item: any, index: number) => (
              <div key={item.acupointName} className="flex items-center justify-between">
                <div className="flex items-center">
                  <span className="text-sm font-medium text-gray-900 w-8">{index + 1}.</span>
                  <span className="text-sm text-gray-700">{item.acupointName}</span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">{item.errorCount}</div>
                  <div className="text-xs text-gray-500">
                    {(item.errorRate * 100).toFixed(2)}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* API性能指标 */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">API性能指标</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">平均延迟</span>
              <span className="text-sm font-medium">{apiLatency.averageLatency}ms</span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">P50 延迟</span>
              <span className="text-sm font-medium">{apiLatency.p50Latency}ms</span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">P95 延迟</span>
              <span className="text-sm font-medium">{apiLatency.p95Latency}ms</span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">P99 延迟</span>
              <span className="text-sm font-medium">{apiLatency.p99Latency}ms</span>
            </div>

            <div className="pt-3 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">错误率</span>
                <span className={`text-sm font-medium ${
                  apiLatency.errorRate > 0.05 ? 'text-red-600' :
                  apiLatency.errorRate > 0.02 ? 'text-yellow-600' : 'text-green-600'
                }`}>
                  {(apiLatency.errorRate * 100).toFixed(2)}%
                </span>
              </div>

              <div className="flex items-center justify-between mt-2">
                <span className="text-sm text-gray-700">总请求数</span>
                <span className="text-sm font-medium">
                  {apiLatency.totalRequests.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 优化建议 */}
      <div className="card p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">AI性能优化建议</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-2">准确率优化</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              {aiPerformance.accuracyRate < 0.85 && (
                <li>• 增加训练数据量</li>
              )}
              {aiPerformance.commonMisidentifications.length > 5 && (
                <li>• 针对常见误识别穴位优化</li>
              )}
              <li>• 考虑模型集成策略</li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-2">用户体验优化</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              {aiCorePerformance.manualCorrectionEffortIndex > 500 && (
                <li>• 减少用户手动修正工作量</li>
              )}
              {aiCorePerformance.confidenceCalibrationScore < 0.8 && (
                <li>• 校准AI自信度显示</li>
              )}
              <li>• 优化交互界面设计</li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-2">性能优化</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              {apiLatency.p95Latency > 3000 && (
                <li>• 优化模型推理速度</li>
              )}
              {apiLatency.errorRate > 0.02 && (
                <li>• 降低API错误率</li>
              )}
              <li>• 考虑边缘计算部署</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiPerformanceView;